/**
 * 手机号登录 Composable
 * 封装完整的手机号登录流程
 */

import { ref, reactive, computed, onUnmounted, type Ref, type ComputedRef } from 'vue'
import type { AuthService } from '../core/services/AuthService'
import { useCountdown } from './useCountdown'

export interface PhoneLoginForm {
  phone: string
  code: string
}

export interface CaptchaForm {
  captchaKey: string
  captchaImage: string
  captchaCode: string
  masterImage?: string
  tileImage?: string
  tileX?: number
  tileY?: number
  slideX?: number
  slideY?: number
}

export interface UsePhoneLoginOptions {
  /** 倒计时秒数，默认 60 */
  countdownSeconds?: number
  /** 登录成功回调 */
  onLoginSuccess?: () => void | Promise<void>
  /** 登录失败回调 */
  onLoginError?: (error: Error) => void
  /** 发送验证码成功回调 */
  onSendCodeSuccess?: () => void
  /** 发送验证码失败回调 */
  onSendCodeError?: (error: Error) => void
}

export interface UsePhoneLoginReturn {
  /** 手机号表单 */
  phoneForm: PhoneLoginForm
  /** 图形验证码表单 */
  captchaForm: CaptchaForm
  /** 是否正在登录 */
  isLoading: Ref<boolean>
  /** 是否已发送验证码 */
  isCodeSent: Ref<boolean>
  /** 倒计时剩余秒数 */
  countdown: Ref<number>
  /** 是否显示图形验证码弹窗 */
  showCaptchaDialog: Ref<boolean>
  /** 是否可以发送验证码 */
  canSendCode: ComputedRef<boolean>
  /** 是否可以登录 */
  canLogin: ComputedRef<boolean>
  /** 发送按钮文字 */
  sendButtonText: ComputedRef<string>
  /** 获取图形验证码 */
  getImageCaptcha: () => Promise<boolean>
  /** 获取滑块验证码 */
  getSlideCaptcha: () => Promise<boolean>
  /** 发送短信验证码 */
  sendCode: () => Promise<boolean>
  /** 发送短信验证码（带滑块验证） */
  sendCodeWithSlide: () => Promise<boolean>
  /** 登录 */
  login: () => Promise<boolean>
  /** 重置表单 */
  reset: () => void
}

/**
 * 创建手机号登录 Composable
 * @param authService 认证服务实例
 * @param options 配置选项
 */
export function createUsePhoneLogin(authService: AuthService) {
  return function usePhoneLogin(options: UsePhoneLoginOptions = {}): UsePhoneLoginReturn {
    const {
      countdownSeconds = 60,
      onLoginSuccess,
      onLoginError,
      onSendCodeSuccess,
      onSendCodeError
    } = options

    // 表单状态
    const phoneForm = reactive<PhoneLoginForm>({
      phone: '',
      code: ''
    })

    const captchaForm = reactive<CaptchaForm>({
      captchaKey: '',
      captchaImage: '',
      captchaCode: '',
      masterImage: '',
      tileImage: '',
      tileX: 0,
      tileY: 0,
      slideX: 0,
      slideY: 0
    })

    // UI 状态
    const isLoading = ref(false)
    const isCodeSent = ref(false)
    const showCaptchaDialog = ref(false)

    // 倒计时
    const {
      remaining: countdown,
      isRunning: isCountdownRunning,
      start: startCountdown,
      stop: stopCountdown,
      reset: resetCountdown
    } = useCountdown({ seconds: countdownSeconds })

    // 计算属性
    const canSendCode = computed(() => {
      return phoneForm.phone.length === 11 && !isCountdownRunning.value && !isLoading.value
    })

    const canLogin = computed(() => {
      return phoneForm.phone.length === 11 && phoneForm.code.length >= 4 && !isLoading.value
    })

    const sendButtonText = computed(() => {
      if (isCountdownRunning.value) {
        return `${countdown.value}s`
      }
      return isCodeSent.value ? '重新获取' : '获取验证码'
    })

    // 获取图形验证码
    const getImageCaptcha = async (): Promise<boolean> => {
      try {
        const result = await authService.getImageCaptcha()
        if (result) {
          captchaForm.captchaImage = result.captcha_image_content
          captchaForm.captchaKey = result.captcha_key
          captchaForm.captchaCode = ''
          showCaptchaDialog.value = true
          return true
        }
        return false
      } catch (error) {
        console.error('[usePhoneLogin] 获取图形验证码失败:', error)
        return false
      }
    }

    // 获取滑块验证码
    const getSlideCaptcha = async (): Promise<boolean> => {
      try {
        const result = await authService.getSlideCaptcha({
          phone: phoneForm.phone,
          smsType: 'login'
        })
        if (result) {
          captchaForm.captchaKey = result.captcha_key
          captchaForm.masterImage = result.master_image
          captchaForm.tileImage = result.tile_image
          captchaForm.tileX = result.tile_x
          captchaForm.tileY = result.tile_y
          captchaForm.slideX = result.tile_x
          captchaForm.slideY = result.tile_y
          showCaptchaDialog.value = true
          return true
        }
        return false
      } catch (error) {
        console.error('[usePhoneLogin] 获取滑块验证码失败:', error)
        return false
      }
    }

    // 发送短信验证码
    const sendCode = async (): Promise<boolean> => {
      if (!canSendCode.value) return false

      try {
        // 保持旧版图片验证码调用语义，避免升级 SDK 后破坏既有项目。
        if (!/^\d{4}$/.test(captchaForm.captchaCode)) {
          return false
        }

        await authService.sendVerifyCodeWithCaptcha({
          phone: phoneForm.phone,
          captcha_key: captchaForm.captchaKey,
          captcha_code: captchaForm.captchaCode,
          smsType: 'login'
        })

        // 发送成功
        showCaptchaDialog.value = false
        isCodeSent.value = true
        startCountdown()

        // 清理图形验证码
        captchaForm.captchaKey = ''
        captchaForm.captchaImage = ''
        captchaForm.captchaCode = ''
        captchaForm.masterImage = ''
        captchaForm.tileImage = ''
        captchaForm.tileX = 0
        captchaForm.tileY = 0
        captchaForm.slideX = 0
        captchaForm.slideY = 0

        onSendCodeSuccess?.()
        return true
      } catch (error) {
        console.error('[usePhoneLogin] 发送验证码失败:', error)
        onSendCodeError?.(error as Error)
        return false
      }
    }

    // 发送短信验证码（滑块验证，新项目显式调用）
    const sendCodeWithSlide = async (): Promise<boolean> => {
      if (!canSendCode.value) return false

      try {
        if (!captchaForm.captchaKey) {
          return false
        }

        await authService.sendVerifyCodeWithSlide({
          phone: phoneForm.phone,
          captcha_key: captchaForm.captchaKey,
          slide_x: captchaForm.slideX ?? captchaForm.tileX ?? 0,
          slide_y: captchaForm.slideY ?? captchaForm.tileY ?? 0,
          smsType: 'login'
        })

        showCaptchaDialog.value = false
        isCodeSent.value = true
        startCountdown()

        captchaForm.captchaKey = ''
        captchaForm.captchaImage = ''
        captchaForm.captchaCode = ''
        captchaForm.masterImage = ''
        captchaForm.tileImage = ''
        captchaForm.tileX = 0
        captchaForm.tileY = 0
        captchaForm.slideX = 0
        captchaForm.slideY = 0

        onSendCodeSuccess?.()
        return true
      } catch (error) {
        console.error('[usePhoneLogin] 发送滑块验证码短信失败:', error)
        onSendCodeError?.(error as Error)
        return false
      }
    }

    // 登录
    const login = async (): Promise<boolean> => {
      if (!canLogin.value) return false

      isLoading.value = true
      try {
        await authService.phoneLogin({
          phone: phoneForm.phone,
          code: phoneForm.code
        })

        // 登录成功
        stopCountdown()
        await onLoginSuccess?.()
        return true
      } catch (error) {
        console.error('[usePhoneLogin] 登录失败:', error)
        onLoginError?.(error as Error)
        return false
      } finally {
        isLoading.value = false
      }
    }

    // 重置表单
    const reset = () => {
      phoneForm.phone = ''
      phoneForm.code = ''
      captchaForm.captchaKey = ''
      captchaForm.captchaImage = ''
      captchaForm.captchaCode = ''
      captchaForm.masterImage = ''
      captchaForm.tileImage = ''
      captchaForm.tileX = 0
      captchaForm.tileY = 0
      captchaForm.slideX = 0
      captchaForm.slideY = 0
      isCodeSent.value = false
      showCaptchaDialog.value = false
      resetCountdown()
    }

    // 组件卸载时清理
    onUnmounted(() => {
      stopCountdown()
    })

    return {
      phoneForm,
      captchaForm,
      isLoading,
      isCodeSent,
      countdown,
      showCaptchaDialog,
      canSendCode,
      canLogin,
      sendButtonText,
      getImageCaptcha,
      getSlideCaptcha,
      sendCode,
      sendCodeWithSlide,
      login,
      reset
    }
  }
}
